import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';
import { InfoMessage } from '@eika/attention-message';
import { Button } from '@eika/button';
import { IconTypes } from '@eika/icon';
import { Loader } from '@eika/loader';
import { TransactionList } from '@eika/transaction-list';

import { EikaModal } from '../../components/EikaModal';
import { Content } from '../../components/shared/Content';
import { ErrorCardWrapper } from '../../components/shared/ErrorCardWrapper';
import { IntersectionTrigger } from '../../components/shared/IntersectionTrigger';
import { TransactionDetails } from '../../components/transaction/TransactionDetails';
import { TransactionFilter } from '../../components/transaction/TransactionFilter';
import {
    TransactionFilterProvider,
    useTransactionFilterContext,
} from '../../components/transaction/TransactionFilter/TransactionFilterContext/TransactionFilterContext';
import { useConfig } from '../../defaultConfig';
import useTransactions from '../../fetchHooks/useTransactions';
import type { Account, AccountId, SearchTransactionsRequest, TransactionId } from '../../generated-types';
import useTranslation from '../../hooks/useTranslation';
import requireMultipleApprovers from '../../utils/boolean/requireMultipleApprovers';
import TransacitionListNoMoreContent from './TransacitionListNoMoreContent';
import TransactionDescription from './TransactionDescription';
import TransactionListSkeleton from './TransactionListSkeleton';
import TransactionListStatus from './TransactionListStatus';
import formatTransactions from './transactionsUtil';

type TransactionsProps = {
    accountId: AccountId;
    account?: Account;
    isLoanView?: boolean;
    setNoAccessToAccountException?: React.Dispatch<React.SetStateAction<RequestExceptionWithResponse | undefined>>;
};

const Transactions: React.FC<TransactionsProps> = ({
    accountId,
    account,
    isLoanView,
    setNoAccessToAccountException,
}) => {
    const translated = useTranslation();
    const [transactionId, setTransactionId] = useState<TransactionId>();
    const [searchFilter, setSearchFilter] = useState<SearchTransactionsRequest>();
    const [showFilterModal, setShowFiltermodal] = useState(false);

    const { featureToggles } = useConfig();

    const {
        isFilteringState: [isFiltering],
        ...transactionFilter
    } = useTransactionFilterContext();
    const { data: transactionPages, ...transactionQuery } = useTransactions(accountId, searchFilter);

    if (transactionQuery.error?.errorName === 'NO_ACCESS_TO_ACCOUNT' && setNoAccessToAccountException) {
        setNoAccessToAccountException(transactionQuery.error);
    }

    useEffect(() => {
        if (isFiltering) {
            setSearchFilter(transactionFilter.transactionSearchPayload);
        } else {
            setSearchFilter(undefined);
        }
    }, [showFilterModal, isFiltering, transactionFilter.transactionSearchPayload]);

    const closeTransactionDetails = () => {
        setTransactionId(undefined);
    };

    const openTransactionDetails = (id: string) => {
        setTransactionId({ value: id });
    };

    const transactions = useMemo(
        () => (transactionPages?.pages ?? []).filter((x) => !!x?.transactions).flatMap((x) => x?.transactions),
        [transactionPages?.pages],
    );

    const multipleApprovers = requireMultipleApprovers(account);

    const getStartBalance = useCallback(() => {
        if (!featureToggles.enableRunningBalance) return undefined;
        if (isFiltering) return undefined;
        if (account?.balance) {
            return account.balance.accountingBalance;
        }
        return undefined;
    }, [account?.balance, featureToggles.enableRunningBalance, isFiltering]);

    const formattedTransactions = useMemo(
        () =>
            formatTransactions(
                transactions,
                openTransactionDetails,
                getStartBalance(),
                isLoanView,
                !isLoanView,
                (transaction) => <TransactionDescription transaction={transaction} />,
                featureToggles.enablePurposeInList,
            ),
        [transactions, getStartBalance, isLoanView, featureToggles.enablePurposeInList],
    );

    const loadMoreTransactions = useCallback(() => {
        if (!transactionQuery.isFetching && transactionQuery.hasNextPage && !transactionQuery.error) {
            transactionQuery.fetchNextPage();
        }
    }, [transactionQuery]);

    const refresh = useCallback(() => {
        transactionQuery.refetch();
    }, [transactionQuery]);

    const isLoading = transactionQuery.isLoading || transactionQuery.isRefetching;

    const isNoContentOrError =
        formattedTransactions.length === 0 && (transactionQuery.isSuccess || transactionQuery.isError);

    const noContent = {
        value: formattedTransactions.length === 0,
        title: searchFilter ? translated.transactionFilter.noHits : translated.transactionListNoTransactionsTitle,
        goToSearch: () => setShowFiltermodal(true),
        isFilter: isFiltering,
    };

    return (
        <>
            {multipleApprovers && !noContent.value && !transactionQuery.error && (
                <InfoMessage
                    icon={IconTypes.INFO_24}
                    className="transactions-list__multiple-approvers-message"
                    text={translated.accountNeedsMultipleApproversTransactionInfoMessage}
                />
            )}
            {featureToggles.enableTransactionFilter && (
                <TransactionFilter
                    title={formattedTransactions.length > 0 ? formattedTransactions[0].label : ''}
                    show={showFilterModal}
                    setShow={setShowFiltermodal}
                    accountId={accountId}
                    getTransactionList={refresh}
                    targetCurrency={account?.currency}
                    showActionButtons={!isLoading}
                />
            )}

            {!isLoading && transactions.length === 0 && (
                <TransactionListStatus error={transactionQuery.error} refresh={refresh} noContent={noContent} />
            )}

            {transactionQuery.isLoading ? (
                <TransactionListSkeleton count={8} />
            ) : (
                <>
                    {!isNoContentOrError && (
                        <>
                            <IntersectionTrigger onBottom={loadMoreTransactions}>
                                <TransactionList
                                    transactions={formattedTransactions.map((transaction, i) => ({
                                        ...transaction,
                                        label: i === 0 ? '' : transaction.label,
                                    }))}
                                    reservedText={translated.reserved}
                                />
                            </IntersectionTrigger>
                            <Content skipBow>
                                <TransacitionListNoMoreContent
                                    show={!transactionQuery.hasNextPage && !isLoanView}
                                    isFilter={!!searchFilter || isFiltering}
                                    goToSearch={() => setShowFiltermodal(true)}
                                />
                                {transactionQuery.isFetchingNextPage && (
                                    <div className="container">
                                        <Loader />
                                    </div>
                                )}
                                {transactionQuery.error && (
                                    <div className="container">
                                        <ErrorCardWrapper
                                            exception={transactionQuery.error}
                                            render={() => (
                                                <p>{translated.transactionListCouldNotLoadMoreTransactions}</p>
                                            )}
                                        >
                                            <Button onClick={loadMoreTransactions}>{translated.tryAgain}</Button>
                                        </ErrorCardWrapper>
                                    </div>
                                )}
                            </Content>
                        </>
                    )}
                </>
            )}
            <EikaModal
                id="transaction-list-modal"
                className="transaction-list-modal"
                open={!!transactionId}
                setOpen={() => null}
                onClose={closeTransactionDetails}
                contentShouldStretch
            >
                <TransactionDetails transactionId={transactionId} accountId={accountId} />
            </EikaModal>
        </>
    );
};

const TransactionsWrapper: React.FC<TransactionsProps> = ({
    setNoAccessToAccountException,
    accountId,
    account,
    isLoanView,
}) => (
    <TransactionFilterProvider>
        <Transactions
            setNoAccessToAccountException={setNoAccessToAccountException}
            accountId={accountId}
            account={account}
            isLoanView={isLoanView}
        />
    </TransactionFilterProvider>
);

export default TransactionsWrapper;
